import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { Typography, Button, TextField, Box } from "@mui/material";
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import type { ColumnDef, TableOptions } from "@tanstack/react-table";
import { useEffect, useRef, useState } from "react";

type CommonReactTableProps<TData> = {
  columns: ColumnDef<TData>[]
  data: TData[]
  state?: Partial<TableOptions<TData>['state']>
  rowId?: keyof TData
  enablePagination?: boolean
  pageSize?: number
  enableSearch?: boolean
}

type DraggableReactTableProps<TData> =
  | (CommonReactTableProps<TData> & {
      draggableRow?: false
      onDraggableRowChange?: never
      getDataOrdered?: never
      orderField?: never
    })
  | {
      draggableRow: true;
      onDraggableRowChange?: (
        draggedRowIndex: number,
        targetRowIndex: number
      ) => void;
      getDataOrdered?: (data: TData[]) => void;
      orderField?: keyof TData | "order";
    };

type ReactTableProps<TData> = CommonReactTableProps<TData> &
  DraggableReactTableProps<TData>;

const ReactTable = <T extends object>({
  columns,
  data,
  rowId,
  state,
  draggableRow = false,
  onDraggableRowChange,
  getDataOrdered,
  enablePagination = false,
  pageSize = 10,
  enableSearch = false
}: ReactTableProps<T>): JSX.Element => {
  const [dataTable, setDataTable] = useState(data)
  const [searchTerm, setSearchTerm] = useState('')
  const draggedRowIndex = useRef<number | null>(null)
  const draggedOverRowIndex = useRef<number | null>(null)

  useEffect(() => {
    setDataTable(
      searchTerm
        ? data.filter(row => Object.values(row).some(value => String(value).toLowerCase().includes(searchTerm.toLowerCase())))
        : data
    )
  }, [searchTerm, data])

  const handleDragStart = (index: number) => {
    draggedRowIndex.current = index
  }

  const handleDragEnter = (index: number) => {
    draggedOverRowIndex.current = index
  }

  const handleDrop = () => {
    if (
      draggedRowIndex.current !== null &&
      draggedOverRowIndex.current !== null &&
      draggedRowIndex.current !== draggedOverRowIndex.current
    ) {
      const updatedData = [...dataTable]
      const draggedRow = updatedData[draggedRowIndex.current]

      updatedData.splice(draggedRowIndex.current, 1)
      updatedData.splice(draggedOverRowIndex.current, 0, draggedRow)

      setDataTable(updatedData)

      if (draggableRow) {
        onDraggableRowChange?.(
          draggedRowIndex.current,
          draggedOverRowIndex.current
        );
        getDataOrdered?.(updatedData);
      }
    }

    draggedRowIndex.current = null
    draggedOverRowIndex.current = null
  }

  const table = useReactTable({
    data: dataTable,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: enablePagination
      ? getPaginationRowModel()
      : undefined,
    getRowId: (row: any) => String(row[rowId]),
    state,
    initialState: enablePagination
      ? { pagination: { pageIndex: 0, pageSize } }
      : undefined,
  });

  return (
    <div id="brand-react-table">
      {enableSearch && (
        <TextField
          id="search-input"
          label="Buscar"
          variant="outlined"
          fullWidth
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          style={{ marginBottom: '10px', width: '210px' }}
        />
      )}
      <Box sx={{ overflowX: "auto", width: "100%" }}>
        <Box sx={{ minWidth: 600 }}>
          <table className="tableBrands_custom">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {draggableRow && <th />}
                  {headerGroup.headers.map((header) => (
                    <th key={header.id} colSpan={header.colSpan}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row, index) => (
                <tr
                  key={row.id}
                  draggable={draggableRow}
                  onDragStart={() => handleDragStart(index)}
                  onDragEnter={() => handleDragEnter(index)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={handleDrop}
                  data-testid="row"
                  style={{
                    cursor: draggableRow ? "grab" : "default",
                    background: "white",
                  }}
                >
                  {draggableRow && (
                    <td>
                      <Button>
                        <DragIndicatorIcon fontSize="small" />
                      </Button>
                    </td>
                  )}
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      </Box>
      {enablePagination && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          className="pagination"
        >
          <Button
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            <Typography>Anterior</Typography>
          </Button>
          <Typography>
            Página {table.getState().pagination.pageIndex + 1} de{" "}
            {table.getPageCount()}
          </Typography>
          <Button
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            <Typography>Siguiente</Typography>
          </Button>
        </div>
      )}
    </div>
  )
}

export default ReactTable
